import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { ExchangeProposalSummary, GuildOffering, GuildOfferingKind, PropertyRecord, SessionProfile } from '@growth-rings/fmos-schema';
import {
  acceptGuildExchange,
  createGuildExchangeProposal,
  listGuildOfferings,
  listMyExchangeProposals,
  saveGuildOffering
} from '../lib/repository';

const offeringKinds: Array<{ value: GuildOfferingKind; label: string }> = [
  { value: 'good', label: 'Product or good' },
  { value: 'service', label: 'Service or skill' },
  { value: 'equipment', label: 'Equipment use' },
  { value: 'space', label: 'Space or access' },
  { value: 'material', label: 'Material' },
  { value: 'right', label: 'Licensed right or permission' },
  { value: 'intellectual_property', label: 'Intellectual property' },
  { value: 'other', label: 'Other exchange value' }
];

function dollars(cents?: number): string {
  if (cents == null) return 'Value discussed by the parties';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export function GuildExchangeView({ profile, property }: { profile: SessionProfile; property?: PropertyRecord }) {
  const [mine, setMine] = useState<GuildOffering[]>([]);
  const [publicOfferings, setPublicOfferings] = useState<GuildOffering[]>([]);
  const [proposals, setProposals] = useState<ExchangeProposalSummary[]>([]);
  const [sku, setSku] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<GuildOfferingKind>('service');
  const [unit, setUnit] = useState('each');
  const [quantity, setQuantity] = useState('1');
  const [value, setValue] = useState('');
  const [publish, setPublish] = useState(false);
  const [cashEnabled, setCashEnabled] = useState(false);
  const [requested, setRequested] = useState<GuildOffering>();
  const [offeredId, setOfferedId] = useState('');
  const [offeredQuantity, setOfferedQuantity] = useState('1');
  const [requestedQuantity, setRequestedQuantity] = useState('1');
  const [proposalTitle, setProposalTitle] = useState('');
  const [proposalMessage, setProposalMessage] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [myItems, discoverable, proposalItems] = await Promise.all([
      listGuildOfferings(profile.id),
      listGuildOfferings(),
      listMyExchangeProposals()
    ]);
    setMine(myItems);
    setPublicOfferings(discoverable.filter((item) => item.ownerProfileId !== profile.id));
    setProposals(proposalItems);
  }

  useEffect(() => {
    void refresh().catch((error) => setMessage(error instanceof Error ? error.message : 'Exchange records could not be loaded.'));
  }, [profile.id]);

  const activeMine = useMemo(() => mine.filter((item) => item.status === 'active' && item.barterEnabled), [mine]);

  async function saveOffering(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setMessage('Saving the Guild offering…');
    try {
      await saveGuildOffering({
        sku: sku.trim().toUpperCase(),
        title,
        description: description || undefined,
        offeringKind: kind,
        unitLabel: unit,
        availableQuantity: quantity ? Number(quantity) : undefined,
        indicativeValueCents: value ? Math.round(Number(value.replaceAll(',', '')) * 100) : undefined,
        currency: 'usd',
        barterEnabled: true,
        cashEnabled,
        visibility: publish ? 'public' : 'private',
        status: publish ? 'active' : 'draft'
      });
      setSku(''); setTitle(''); setDescription(''); setValue(''); setPublish(false);
      setMessage(publish ? 'Offering published under your Guild sign.' : 'Private offering draft saved.');
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The offering could not be saved.');
    } finally { setBusy(false); }
  }

  async function propose(event: FormEvent) {
    event.preventDefault();
    if (!requested || !offeredId) return;
    setBusy(true); setMessage('Recording the exchange proposal…');
    try {
      await createGuildExchangeProposal({
        counterpartyProfileId: requested.ownerProfileId,
        title: proposalTitle || `Exchange for ${requested.title}`,
        message: proposalMessage || undefined,
        offeredOfferingId: offeredId,
        offeredQuantity: Number(offeredQuantity),
        requestedOfferingId: requested.id,
        requestedQuantity: Number(requestedQuantity),
        propertyId: property?.id
      });
      setRequested(undefined); setProposalTitle(''); setProposalMessage('');
      setMessage('Exchange proposed. Both parties must accept the same recorded items and values before it becomes an agreement.');
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The exchange proposal could not be recorded.');
    } finally { setBusy(false); }
  }

  async function accept(proposalId: string) {
    setBusy(true); setMessage('Recording your acceptance…');
    try {
      await acceptGuildExchange(proposalId);
      setMessage('Your acceptance was recorded. The agreement becomes final only after both parties accept the same snapshot.');
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Acceptance could not be recorded.');
    } finally { setBusy(false); }
  }

  return <section className="panel guild-exchange">
    <div className="panel-heading"><div><span className="pill orange">Guild exchange</span><h2>Record what you can offer—and what another member can provide.</h2><p>Goods, services, equipment, space and intellectual property can be valued and exchanged without Growth Rings creating a private currency. Each agreement preserves both parties’ descriptions, quantities and valuations.</p></div></div>
    {message && <p role="status" className="billing-notice">{message}</p>}

    <details>
      <summary><strong>Add a product, service or other offering</strong></summary>
      <form className="create-form" onSubmit={saveOffering}>
        <div className="form-grid">
          <label>SKU or reference<input value={sku} onChange={(event) => setSku(event.target.value)} placeholder="HONEY-GAL or ELEC-COMM" required /></label>
          <label>Offering type<select value={kind} onChange={(event) => setKind(event.target.value as GuildOfferingKind)}>{offeringKinds.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          <label className="full-field">Title<input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="One gallon of raw honey" required /></label>
          <label className="full-field">Description<textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Quality, scope, provenance, conditions or limitations." /></label>
          <label>Unit<input value={unit} onChange={(event) => setUnit(event.target.value)} placeholder="gallon, hour, project, day" required /></label>
          <label>Available quantity<input type="number" min="0" step="0.001" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
          <label>Indicative value in USD<input inputMode="decimal" value={value} onChange={(event) => setValue(event.target.value)} placeholder="0.00" /></label>
          <label><input type="checkbox" checked={cashEnabled} onChange={(event) => setCashEnabled(event.target.checked)} /> Cash may supplement the exchange</label>
        </div>
        <label className="permission-row"><input type="checkbox" checked={publish} onChange={(event) => setPublish(event.target.checked)} /><span><strong>Publish under my Guild sign.</strong> Free accounts can prepare one private draft; publishing and exchange proposals require an eligible paid membership.</span></label>
        <button className="button primary" disabled={busy}>{publish ? 'Publish offering' : 'Save private draft'}</button>
      </form>
    </details>

    <div className="guild-layout">
      <div>
        <h3>Your offerings</h3>
        <div className="activity-list">{mine.length ? mine.map((item) => <div className="activity" key={item.id}><span className="activity-dot"></span><div><strong>{item.sku} · {item.title}</strong><span>{item.availableQuantity ?? 'Flexible'} {item.unitLabel} · {dollars(item.indicativeValueCents)} · {item.visibility}</span></div><time>{item.status}</time></div>) : <p>No offerings recorded yet.</p>}</div>
      </div>
      <div>
        <h3>Open exchange proposals</h3>
        <div className="activity-list">{proposals.length ? proposals.map((proposal) => <div className="activity" key={proposal.id}><span className="activity-dot"></span><div><strong>{proposal.title}</strong><span>{proposal.message || 'Recorded Guild exchange'}</span><button className="button secondary" disabled={busy || proposal.status === 'accepted'} onClick={() => void accept(proposal.id)}>{proposal.status === 'accepted' ? 'Accepted agreement' : 'Review and accept recorded snapshot'}</button></div><time>{proposal.status}</time></div>) : <p>No exchange proposals yet.</p>}</div>
      </div>
    </div>

    <h3>Discover exchange-ready offerings</h3>
    <div className="guild-grid">{publicOfferings.length ? publicOfferings.map((item) => <article className="guild-card" key={item.id}><span className="pill">{item.offeringKind.replaceAll('_', ' ')}</span><h3>{item.title}</h3><p>{item.description || 'Details available from the Guild member.'}</p><dl className="guild-facts"><div><dt>Reference</dt><dd>{item.sku}</dd></div><div><dt>Unit</dt><dd>{item.unitLabel}</dd></div><div><dt>Recorded value</dt><dd>{dollars(item.indicativeValueCents)}</dd></div></dl><button className="button primary" onClick={() => { setRequested(item); setProposalTitle(`Exchange for ${item.title}`); }}>Propose an exchange</button></article>) : <p>No public exchange offerings have been published yet.</p>}</div>

    {requested && <form className="create-form quote-draft-box" onSubmit={propose}>
      <h3>Propose an exchange for {requested.title}</h3>
      <label>Your offering<select value={offeredId} onChange={(event) => setOfferedId(event.target.value)} required><option value="">Choose one of your active offerings</option>{activeMine.map((item) => <option key={item.id} value={item.id}>{item.sku} · {item.title}</option>)}</select></label>
      <div className="form-grid"><label>Your quantity<input type="number" min="0.001" step="0.001" value={offeredQuantity} onChange={(event) => setOfferedQuantity(event.target.value)} required /></label><label>Requested quantity<input type="number" min="0.001" step="0.001" value={requestedQuantity} onChange={(event) => setRequestedQuantity(event.target.value)} required /></label></div>
      <label>Proposal title<input value={proposalTitle} onChange={(event) => setProposalTitle(event.target.value)} required /></label>
      <label>Terms or context<textarea value={proposalMessage} onChange={(event) => setProposalMessage(event.target.value)} placeholder="Describe timing, delivery, project relationship, valuation assumptions and any cash supplement." /></label>
      <p className="fine">The displayed dollar values document the parties’ understanding. They are not a Growth Rings appraisal or tax determination.</p>
      <div className="button-row"><button className="button primary" disabled={busy || !offeredId}>Record proposal</button><button type="button" className="button secondary" onClick={() => setRequested(undefined)}>Cancel</button></div>
    </form>}
  </section>;
}
